import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useCustomer } from '../../context/CustomerContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Shield, Plus, Clock, AlertCircle, CheckCircle, 
  XCircle, FileText, AlertTriangle 
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AccountWarranty = () => {
  const { getAuthHeaders } = useCustomer();
  const [searchParams] = useSearchParams();
  
  const [warranties, setWarranties] = useState([]);
  const [claims, setClaims] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('warranties');
  
  // Registration form
  const [showRegister, setShowRegister] = useState(false);
  const [registerForm, setRegisterForm] = useState({
    order_id: '',
    email: '',
    phone: ''
  });
  
  // Claim form
  const [showClaim, setShowClaim] = useState(false);
  const [claimForm, setClaimForm] = useState({
    warranty_id: '',
    issue_description: ''
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchData();
    
    // Check if we should open registration form
    const orderId = searchParams.get('order');
    if (orderId) {
      setShowRegister(true);
      setRegisterForm(prev => ({ ...prev, order_id: orderId }));
    }
  }, []);

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const [warrantiesRes, claimsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/warranty`, { headers }),
        axios.get(`${API}/warranty/claims/list`, { headers }),
        axios.get(`${API}/customer/orders?limit=50`, { headers })
      ]);
      
      setWarranties(warrantiesRes.data.warranties || []);
      setClaims(claimsRes.data.claims || []);
      setOrders(ordersRes.data.orders || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/warranty/register`, registerForm, {
        headers: getAuthHeaders()
      });
      setSuccess(`Warranty registered! Number: ${response.data.warranty_number}`);
      setShowRegister(false);
      setRegisterForm({ order_id: '', email: '', phone: '' });
      fetchData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to register warranty');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClaim = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/warranty/claim`, claimForm, {
        headers: getAuthHeaders()
      });
      setSuccess(`Claim submitted! Number: ${response.data.claim_number}`);
      setShowClaim(false);
      setClaimForm({ warranty_id: '', issue_description: '' });
      fetchData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to submit claim');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      expired: 'bg-gray-100 text-gray-800',
      voided: 'bg-red-100 text-red-800',
      submitted: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      denied: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-stone-100 text-stone-800';
  };

  // Filter orders that don't have warranties
  const eligibleOrders = orders.filter(order => 
    !warranties.some(w => w.order_id === order.id)
  );

  // Filter active warranties for claims
  const activeWarranties = warranties.filter(w => w.status === 'active' && !w.is_expired);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="account-warranty">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-serif text-stone-800">Warranty Center</h2>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-md flex items-center gap-2 text-green-700">
          <CheckCircle className="h-4 w-4" />
          {success}
        </div>
      )}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2 text-red-700">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => setShowRegister(true)}
        >
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 bg-amber-100 rounded-full">
              <Shield className="h-6 w-6 text-amber-700" />
            </div>
            <div>
              <h3 className="font-semibold text-stone-800">Register Warranty</h3>
              <p className="text-sm text-stone-500">Protect your purchase</p>
            </div>
            <Plus className="h-5 w-5 ml-auto text-stone-400" />
          </CardContent>
        </Card>
        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => setShowClaim(true)}
        >
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 bg-orange-100 rounded-full">
              <FileText className="h-6 w-6 text-orange-700" />
            </div>
            <div>
              <h3 className="font-semibold text-stone-800">File a Claim</h3>
              <p className="text-sm text-stone-500">Report an issue</p>
            </div>
            <Plus className="h-5 w-5 ml-auto text-stone-400" />
          </CardContent>
        </Card>
      </div>

      {/* Registration Modal */}
      {showRegister && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-serif">Register Warranty</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="space-y-2">
                <Label>Select Order</Label>
                <Select
                  value={registerForm.order_id}
                  onValueChange={(v) => setRegisterForm(prev => ({ ...prev, order_id: v }))}
                >
                  <SelectTrigger data-testid="warranty-order-select">
                    <SelectValue placeholder="Choose an order" />
                  </SelectTrigger>
                  <SelectContent>
                    {eligibleOrders.length === 0 ? (
                      <div className="p-2 text-sm text-stone-500">
                        All orders have warranties registered
                      </div>
                    ) : (
                      eligibleOrders.map(order => (
                        <SelectItem key={order.id} value={order.id}>
                          {order.order_number} - {new Date(order.created_at).toLocaleDateString()}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="warranty-email">Contact Email</Label>
                <Input
                  id="warranty-email"
                  type="email"
                  value={registerForm.email}
                  onChange={(e) => setRegisterForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="your@email.com"
                  required
                  data-testid="warranty-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="warranty-phone">Phone (Optional)</Label>
                <Input
                  id="warranty-phone"
                  type="tel"
                  value={registerForm.phone}
                  onChange={(e) => setRegisterForm(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+1 (555) 000-0000"
                  data-testid="warranty-phone-input"
                />
              </div>
              <div className="flex gap-3">
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => setShowRegister(false)}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-amber-700 hover:bg-amber-800"
                  disabled={submitting || !registerForm.order_id}
                  data-testid="register-warranty-btn"
                >
                  {submitting ? 'Registering...' : 'Register Warranty'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Claim Modal */}
      {showClaim && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-serif">File Warranty Claim</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleClaim} className="space-y-4">
              {activeWarranties.length === 0 ? (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                  <span className="text-yellow-800">No active warranties available for claims</span>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>Select Warranty</Label>
                    <Select
                      value={claimForm.warranty_id}
                      onValueChange={(v) => setClaimForm(prev => ({ ...prev, warranty_id: v }))}
                    >
                      <SelectTrigger data-testid="claim-warranty-select">
                        <SelectValue placeholder="Choose a warranty" />
                      </SelectTrigger>
                      <SelectContent>
                        {activeWarranties.map(warranty => (
                          <SelectItem key={warranty.id} value={warranty.id}>
                            {warranty.warranty_number} - Order {warranty.order_number}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="claim-description">Describe the Issue</Label>
                    <Textarea
                      id="claim-description"
                      value={claimForm.issue_description}
                      onChange={(e) => setClaimForm(prev => ({ ...prev, issue_description: e.target.value }))}
                      placeholder="Please describe the problem with your product..."
                      rows={4}
                      required
                      data-testid="claim-description-input"
                    />
                  </div>
                </>
              )}
              <div className="flex gap-3">
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => setShowClaim(false)}
                >
                  Cancel
                </Button>
                {activeWarranties.length > 0 && (
                  <Button 
                    type="submit" 
                    className="bg-amber-700 hover:bg-amber-800"
                    disabled={submitting || !claimForm.warranty_id}
                    data-testid="submit-claim-btn"
                  >
                    {submitting ? 'Submitting...' : 'Submit Claim'}
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="warranties">My Warranties ({warranties.length})</TabsTrigger>
          <TabsTrigger value="claims">My Claims ({claims.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="warranties" className="space-y-4">
          {warranties.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Shield className="h-16 w-16 mx-auto mb-4 text-stone-300" />
                <h3 className="text-lg font-medium text-stone-800 mb-2">No warranties</h3>
                <p className="text-stone-500">Register a warranty to protect your purchase</p>
              </CardContent>
            </Card>
          ) : (
            warranties.map(warranty => (
              <Card key={warranty.id}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="font-semibold text-stone-800">{warranty.warranty_number}</p>
                      <p className="text-sm text-stone-500">Order: {warranty.order_number}</p>
                    </div>
                    <Badge className={getStatusColor(warranty.is_expired ? 'expired' : warranty.status)}>
                      {warranty.is_expired ? 'Expired' : warranty.status}
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-stone-500">Purchase Date</p>
                      <p className="font-medium text-stone-700">
                        {new Date(warranty.purchase_date).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-stone-500">Expires</p>
                      <p className="font-medium text-stone-700">
                        {new Date(warranty.expiration_date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  {warranty.days_remaining !== undefined && !warranty.is_expired && (
                    <div className="mt-4 p-3 bg-green-50 rounded-lg">
                      <p className="text-sm text-green-700">
                        <CheckCircle className="h-4 w-4 inline mr-1" />
                        {warranty.days_remaining} days remaining
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="claims" className="space-y-4">
          {claims.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <FileText className="h-16 w-16 mx-auto mb-4 text-stone-300" />
                <h3 className="text-lg font-medium text-stone-800 mb-2">No claims</h3>
                <p className="text-stone-500">You haven't filed any warranty claims</p>
              </CardContent>
            </Card>
          ) : (
            claims.map(claim => (
              <Card key={claim.id}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="font-semibold text-stone-800">{claim.claim_number}</p>
                      <p className="text-sm text-stone-500">Warranty: {claim.warranty_number}</p>
                      <p className="text-sm text-stone-500 flex items-center gap-1 mt-1">
                        <Clock className="h-4 w-4" />
                        {new Date(claim.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Badge className={getStatusColor(claim.status)}>
                      {claim.status}
                    </Badge>
                  </div>
                  
                  <div className="bg-stone-50 rounded-lg p-3">
                    <p className="text-sm text-stone-600">{claim.issue_description}</p>
                  </div>

                  {claim.resolution && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm font-medium text-blue-800">Resolution:</p>
                      <p className="text-sm text-blue-700">{claim.admin_notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AccountWarranty;
